import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import { productsApi, categoriesApi, brandsApi, unitsApi, warehousesApi } from '@/services';
import api from '@/services/api';

interface Pagination {
  currentPage: number;
  lastPage: number;
  perPage: number;
  total: number;
}

/**
 * Backend meta envelope (camelCased by the api.ts interceptor). Laravel
 * paginators emit `lastPage`; some endpoints surface `totalPages` instead,
 * so both names are declared — the reducers below read them in that order.
 */
interface PaginationMeta {
  currentPage?: number;
  lastPage?: number;
  totalPages?: number;
  perPage?: number;
  total?: number;
}

/**
 * Loose inventory entity. The API resources carry module-specific columns
 * (pricing, batches, taxes, relations) beyond the identity fields the reducers
 * actually touch, so the remaining keys stay an open record — the documented
 * `any` at this API boundary, rather than a false-complete model.
 */
interface InventoryEntity {
  id: number | string;
  name?: string;
  [key: string]: any;
}

const initialPagination: Pagination = {
  currentPage: 1,
  lastPage: 1,
  perPage: 10,
  total: 0,
};

interface InventoryState {
  products: InventoryEntity[];
  product: InventoryEntity | null;
  categories: InventoryEntity[];
  brands: InventoryEntity[];
  warehouses: InventoryEntity[];
  units: InventoryEntity[];
  inventoryTransactions: InventoryEntity[];
  pagination: Pagination;
  loading: boolean;
  loadingCount: number;
  error: unknown;
}

const initialState: InventoryState = {
  products: [],
  product: null,
  categories: [],
  brands: [],
  warehouses: [],
  units: [],
  inventoryTransactions: [],
  pagination: { ...initialPagination },
  loading: false,
  loadingCount: 0,
  error: null,
};

export const fetchProducts = createAsyncThunk(
  'inventory/fetchProducts',
  async (params: Record<string, any> | undefined, { rejectWithValue }) => {
    try {
      const response: any = await productsApi.getAll(params || {});
      return {
        data: (response?.data?.data ?? []) as InventoryEntity[],
        meta: (response?.data?.meta ?? {}) as PaginationMeta,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchProduct = createAsyncThunk(
  'inventory/fetchProduct',
  async (id: number | string, { rejectWithValue }) => {
    try {
      const response: any = await productsApi.getById(id);
      return (response?.data?.data ?? null) as InventoryEntity | null;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createProduct = createAsyncThunk(
  'inventory/createProduct',
  async (data: Record<string, any>, { rejectWithValue }) => {
    try {
      const response: any = await productsApi.create(data);
      return (response?.data?.data ?? null) as InventoryEntity | null;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateProduct = createAsyncThunk(
  'inventory/updateProduct',
  async ({ id, data }: { id: number | string, data: Record<string, any> }, { rejectWithValue }) => {
    try {
      const response: any = await productsApi.update(id, data);
      return (response?.data?.data ?? null) as InventoryEntity | null;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteProduct = createAsyncThunk(
  'inventory/deleteProduct',
  async (id: number | string, { rejectWithValue }) => {
    try {
      await productsApi.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchCategories = createAsyncThunk(
  'inventory/fetchCategories',
  async (params: Record<string, any> | undefined, { rejectWithValue }) => {
    try {
      const response: any = await categoriesApi.getAll(params || {});
      return (response?.data?.data ?? []) as InventoryEntity[];
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchBrands = createAsyncThunk(
  'inventory/fetchBrands',
  async (params: Record<string, any> | undefined, { rejectWithValue }) => {
    try {
      const response: any = await brandsApi.getAll(params || {});
      return (response?.data?.data ?? []) as InventoryEntity[];
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchWarehouses = createAsyncThunk(
  'inventory/fetchWarehouses',
  async (params: Record<string, any> | undefined, { rejectWithValue }) => {
    try {
      const response: any = await warehousesApi.getAll(params || {});
      const data = response?.data?.data;
      return (Array.isArray(data?.warehouses) ? data.warehouses : Array.isArray(data) ? data : []) as InventoryEntity[];
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchUnits = createAsyncThunk(
  'inventory/fetchUnits',
  async (params: Record<string, any> | undefined, { rejectWithValue }) => {
    try {
      const response: any = await unitsApi.getAll(params || {});
      return (response?.data?.data ?? []) as InventoryEntity[];
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchInventoryTransactions = createAsyncThunk(
  'inventory/fetchInventoryTransactions',
  async ({ productId, params }: { productId: number | string, params?: Record<string, any> }, { rejectWithValue }) => {
    try {
      const response: any = await api.get(`/products/${productId}/inventory-transactions`, { params: params || {} });
      return (response?.data?.data ?? []) as InventoryEntity[];
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const inventorySlice = createSlice({
  name: 'inventory',
  initialState,
  reducers: {
    clearProduct(state) {
      state.product = null;
    },
    clearError(state) {
      state.error = null;
    },
    clearState() {
      // Fresh object: returning the module-level constant aliases it into live
      // Redux state (and immer freezes it), so any later in-place mutation would
      // corrupt the baseline for every subsequent reset.
      return { ...initialState, pagination: { ...initialPagination } };
    },
  },
  extraReducers: (builder) => {
    // fetchProducts can run concurrently (a refetch while a previous page is
    // still in flight). A plain boolean flipped false the moment the first
    // response landed, hiding the still-running request — an in-flight counter
    // keeps loading true until every pending fetch settles. Mutations
    // (create/update/delete) never touch loading, so their reject handler is
    // the plain error setter, not the fetch-balanced endFetch.
    const beginFetch = (state: InventoryState) => {
      state.loadingCount += 1;
      state.loading = true;
      state.error = null;
    };
    const endFetch = (state: InventoryState) => {
      state.loadingCount = Math.max(0, state.loadingCount - 1);
      state.loading = state.loadingCount > 0;
    };
    const handleRejected = (state: InventoryState, action: PayloadAction<unknown>) => {
      state.error = action.payload;
    };
    const handleFetchRejected = (state: InventoryState, action: PayloadAction<unknown>) => {
      endFetch(state);
      state.error = action.payload;
    };

    builder
      // Fetch Products
      .addCase(fetchProducts.pending, beginFetch)
      .addCase(fetchProducts.fulfilled, (state, action) => {
        endFetch(state);
        state.products = action.payload.data;
        const meta = action.payload.meta;
        state.pagination = {
          currentPage: meta.currentPage || 1,
          lastPage: meta.totalPages || meta.lastPage || 1,
          perPage: meta.perPage || 10,
          // NOT `products.length`: on page 2+ that is the page size, so the grid
          // header would claim "total 10" for a 100-row catalogue. Keep the last
          // known total when the API omits it.
          total: meta.total ?? state.pagination.total,
        };
      })
      .addCase(fetchProducts.rejected, handleFetchRejected)
      // Fetch Product
      .addCase(fetchProduct.fulfilled, (state, action) => {
        state.product = action.payload;
      })
      .addCase(fetchProduct.rejected, handleRejected)
      // Create Product
      .addCase(createProduct.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.products.unshift(action.payload);
        state.pagination.total += 1;
        state.pagination.lastPage = Math.max(
          state.pagination.lastPage,
          Math.ceil(state.pagination.total / Math.max(1, state.pagination.perPage)),
        );
      })
      .addCase(createProduct.rejected, handleRejected)
      // Update Product
      .addCase(updateProduct.fulfilled, (state, action) => {
        const updated = action.payload;
        if (!updated) return;
        const index = state.products.findIndex((p: any) => p.id === updated.id);
        if (index !== -1) {
          state.products[index] = updated;
        }
        if (state.product?.id === updated.id) {
          state.product = updated;
        }
      })
      .addCase(updateProduct.rejected, handleRejected)
      // Delete Product
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.products = state.products.filter((p: any) => p.id !== action.payload);
        if (state.product?.id === action.payload) {
          state.product = null;
        }
        state.pagination.total = Math.max(0, state.pagination.total - 1);
        state.pagination.lastPage = Math.max(
          1,
          Math.ceil(state.pagination.total / Math.max(1, state.pagination.perPage)),
        );
      })
      .addCase(deleteProduct.rejected, handleRejected)
      // Categories
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categories = action.payload;
      })
      .addCase(fetchCategories.rejected, handleRejected)
      // Brands
      .addCase(fetchBrands.fulfilled, (state, action) => {
        state.brands = action.payload;
      })
      .addCase(fetchBrands.rejected, handleRejected)
      // Warehouses
      .addCase(fetchWarehouses.fulfilled, (state, action) => {
        state.warehouses = action.payload;
      })
      .addCase(fetchWarehouses.rejected, handleRejected)
      // Units
      .addCase(fetchUnits.fulfilled, (state, action) => {
        state.units = action.payload;
      })
      .addCase(fetchUnits.rejected, handleRejected)
      // Inventory Transactions
      .addCase(fetchInventoryTransactions.fulfilled, (state, action) => {
        state.inventoryTransactions = action.payload;
      })
      .addCase(fetchInventoryTransactions.rejected, handleRejected);
  },
});

export const { clearProduct, clearError, clearState } = inventorySlice.actions;
export default inventorySlice.reducer;
